import React, { useEffect, useState } from "react";
import { getTransparencyById, getTransparency } from "../../app/api";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const HTMLTextView = ({ htmlContent }: any) => {
  if (!htmlContent) return null;
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

export default function TransparencyDetail() {
  const [loading, setLoading] = useState(false);
  const [transparency, setTransparency] = useState<any>(null);
  const [children, setChildren] = useState<any[]>([]);
  const params = useParams();

  useEffect(() => {
    setLoading(true);
    
    // Fetch transparency detail
    getTransparencyById(params.id)
      .then((res: any) => {
        if (res && res.data) {
          setTransparency(res.data);
          
          // Fetch children if exists
          if (res.data._id) {
            getTransparency({ parent_id: res.data._id, limit: 50, offset: 0 })
              .then((childRes: any) => {
                if (childRes && childRes.data && childRes.data.results) {
                  setChildren(childRes.data.results);
                }
              })
              .catch((err) => {
                console.error("Error fetching children:", err);
              });
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching transparency:", err);
      })
      .finally(() => {
        setLoading(false);
      });
    
    window.document.body.scrollTop = 0;
    window.document.documentElement.scrollTop = 0;
  }, [params.id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-primary-50 via-fun-50 to-happy-50 flex items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-playful-300 border-t-primary-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl">✨</span>
          </div>
        </div>
      </div>
    );
  }

  if (!transparency) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-primary-50 via-fun-50 to-happy-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-12 text-center shadow-large border-4 border-primary-200">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-xl text-dark-600">
              Ил тод байдлын мэдээлэл олдсонгүй.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-primary-50 via-fun-50 to-happy-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/transparency"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-primary-600 font-semibold mb-8 hover:bg-primary-50 shadow-soft hover:shadow-medium transform hover:scale-105 transition-all duration-300"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Буцах
        </Link>

        <div className="mx-auto max-w-4xl">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-large border-4 border-primary-200 mb-8 animate-fade-in">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-dark-900 mb-8">
              {transparency.name}
            </h1>

            {transparency.content && (
              <div className="prose prose-lg max-w-none mb-8 text-dark-700 leading-relaxed">
                <HTMLTextView htmlContent={transparency.content} />
              </div>
            )}

            {transparency.pdf_url && (
              <div className="mb-8 rounded-2xl overflow-hidden shadow-large border-4 border-fun-200">
                <iframe
                  src={transparency.pdf_url}
                  className="w-full h-[600px] md:h-[800px]"
                  title="PDF Viewer"
                />
              </div>
            )}

            {transparency.link && (
              <div className="mb-8">
                <a
                  href={transparency.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-500 to-fun-500 text-white font-bold shadow-medium hover:shadow-glow hover:scale-105 transform transition-all duration-300"
                >
                  <span>🔗</span>
                  <span>Холбоос нээх</span>
                  <span>→</span>
                </a>
              </div>
            )}
          </div>

          {children.length > 0 && (
            <div className="mt-12 animate-slide-up">
              <h2 className="text-2xl sm:text-3xl font-bold text-dark-900 mb-8 text-center">
                <span className="text-gradient">📂 Дэд ангилал</span>
              </h2>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {children.map((child: any, index: number) => (
                  <Link
                    key={child._id || child.id}
                    to={`/transparency/${child._id || child.id}`}
                    className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-large border-4 border-transparent hover:border-primary-200 transform hover:-translate-y-2 transition-all duration-300 animate-scale-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <h3 className="text-xl font-bold text-dark-900 mb-3 group-hover:text-primary-600 transition-colors">
                      {child.name}
                    </h3>
                    {child.content && (
                      <p className="text-dark-600 text-sm line-clamp-3">
                        <HTMLTextView htmlContent={child.content.substring(0, 150) + '...'} />
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

